import { z } from 'zod';
import { PAYMENT_METHODS, PAYMENT_STATUSES, WALLET_TOP_UP_METHODS, WALLET_TRANSACTION_KINDS } from '@bulk/types';
import { idSchema, paginationQuerySchema } from './common';

export const MAX_WALLET_AMOUNT_MINOR = 1_000_000;

export const walletTopUpSchema = z.object({
  amountMinor: z.number().int().min(1).max(MAX_WALLET_AMOUNT_MINOR),
  method: z.enum(WALLET_TOP_UP_METHODS),
  note: z.string().trim().max(500).optional(),
});
export type WalletTopUpInput = z.infer<typeof walletTopUpSchema>;

export const walletCorrectionSchema = z.object({
  amountMinor: z
    .number()
    .int()
    .min(-MAX_WALLET_AMOUNT_MINOR)
    .max(MAX_WALLET_AMOUNT_MINOR)
    .refine((n) => n !== 0, 'A correction changes the balance')
    .describe('Positive adds to the balance, negative takes away; the balance never goes below zero'),
  reason: z.string().trim().min(3).max(500),
});
export type WalletCorrectionInput = z.infer<typeof walletCorrectionSchema>;

export const walletSchema = z.object({
  memberId: idSchema,
  balanceMinor: z.number().int(),
  currency: z.string(),
  updatedAt: z.string().nullable(),
  onlineTopUp: z
    .object({ available: z.boolean(), minMinor: z.number().int(), maxMinor: z.number().int() })
    .nullable()
    .optional()
    .describe('Only on the member\'s own balance: whether they can top it up online now, and within what amounts'),
});
export type WalletDto = z.infer<typeof walletSchema>;

export const walletTransactionSchema = z.object({
  id: idSchema,
  kind: z.enum(WALLET_TRANSACTION_KINDS),
  amountMinor: z.number().int().describe('Signed: money in is positive, money spent is negative'),
  balanceAfterMinor: z.number().int(),
  currency: z.string(),
  method: z.enum(PAYMENT_METHODS).nullable(),
  locationName: z.string().nullable(),
  barSaleId: idSchema.nullable(),
  items: z.array(z.object({ name: z.record(z.string(), z.string()), quantity: z.number().int() })).describe('What a bar line was for (what was refunded, on a refund); empty for anything else'),
  note: z.string().nullable(),
  recordedByName: z.string().nullable().describe('Who recorded it; null in a member\'s own history'),
  createdAt: z.string(),
});
export type WalletTransactionDto = z.infer<typeof walletTransactionSchema>;

export const walletMovementResultSchema = z.object({
  wallet: walletSchema,
  transaction: walletTransactionSchema,
});
export type WalletMovementResultDto = z.infer<typeof walletMovementResultSchema>;

export const walletTransactionsQuerySchema = paginationQuerySchema;

export const onlineTopUpSchema = z.object({
  amountMinor: z.number().int().min(1).max(MAX_WALLET_AMOUNT_MINOR),
  returnUrl: z.url().optional(),
  locale: z.enum(['en', 'ka']).optional(),
});
export type OnlineTopUpInput = z.infer<typeof onlineTopUpSchema>;

export const onlineTopUpResultSchema = z.object({
  paymentId: idSchema,
  redirectUrl: z.string().nullable(),
  status: z.enum(PAYMENT_STATUSES),
});
export type OnlineTopUpResultDto = z.infer<typeof onlineTopUpResultSchema>;
export type WalletTransactionsQuery = z.infer<typeof walletTransactionsQuerySchema>;
